import type { DriftIssue, ProjectModel } from "../../types.js";
import { loadScaffoldText } from "./shared.js";

/** npm lifecycle hooks and internal scripts that don't need documentation */
const IGNORED_SCRIPTS = new Set([
  "preinstall",
  "install",
  "postinstall",
  "preuninstall",
  "uninstall",
  "postuninstall",
  "prepublish",
  "prepublishOnly",
  "publish",
  "postpublish",
  "prepack",
  "pack",
  "postpack",
  "prepare",
  "preshrinkwrap",
  "shrinkwrap",
  "postshrinkwrap",
]);

/**
 * Check that package.json scripts are mentioned somewhere in the scaffold.
 * Reads scripts from `project.rootManifest.scripts` (already populated by the
 * scanner) — no disk re-read.
 */
export function checkScriptCoverage(
  scaffoldFiles: string[],
  project: ProjectModel | undefined
): DriftIssue[] {
  if (project?.rootManifest?.type !== "package.json") return [];
  const scripts = new Set(Object.keys(project.rootManifest.scripts));
  if (scripts.size === 0) return [];

  const scaffoldText = loadScaffoldText(scaffoldFiles);
  const issues: DriftIssue[] = [];

  for (const script of scripts) {
    // Skip lifecycle hooks
    if (IGNORED_SCRIPTS.has(script)) continue;

    // Skip pre/post variants of other scripts (e.g. pretest, postbuild)
    if (
      (script.startsWith("pre") && scripts.has(script.slice(3))) ||
      (script.startsWith("post") && scripts.has(script.slice(4)))
    ) {
      continue;
    }

    // Skip colon variants if the base script is documented (e.g. dev:debug when dev exists)
    if (script.includes(":")) {
      const base = script.split(":")[0];
      if (scaffoldText.includes(base)) continue;
    }

    // Check if the script name appears anywhere in scaffold files
    if (!scaffoldText.includes(script)) {
      issues.push({
        code: "UNDOCUMENTED_SCRIPT",
        severity: "warning",
        file: "package.json",
        line: null,
        message: `Script "${script}" exists in package.json but is not mentioned in any scaffold file`,
        explanation: "This script exists in package.json but does not appear in the scaffold text used to guide the agent.",
        suggestion: `Mention "${script}" in setup or workflow documentation if agents are expected to use it.`,
      });
    }
  }

  return issues;
}